// Permisos por miembro del equipo — ADR-2026-10-19-permisos-por-miembro-del-equipo.
//
// El dueño de la cuenta restringe a cada miembro (Configuración › Equipo): si puede publicar en
// la tienda online y qué marcas ve. Los candados de verdad están en otro lado —las marcas en la
// RLS de la base, la tienda en el backend (403 `sin_permiso_tienda`)—; esto es lo que usa el
// front para no ofrecer lo que después va a rebotar, y para decirle al miembro a quién pedírselo.
//
// Sin fila en `permisos_de_miembro` = sin restricciones (es lo que tiene el 99 % de las cuentas).

import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { esDuenoDeLaCuenta } from './duenoDeLaCuenta';

export interface MisPermisos {
  puedePublicarEnTienda: boolean;
  /** `null` = todas las marcas. */
  marcas: string[] | null;
}

export const TODO_PERMITIDO: MisPermisos = { puedePublicarEnTienda: true, marcas: null };

export interface ContactoDelDueno {
  nombre: string;
  email: string | null;
}

export interface CuentaYPermisos {
  permisos: MisPermisos;
  /** `false` también cuando no se pudo leer la empresa (no se afirma nada: ver `esDuenoDeLaCuenta`). */
  esDueno: boolean;
  /**
   * Se SABE que el dueño es otra persona. Para el copy ("pedíselo a…"): ante la duda se le
   * habla como dueño, que es lo que veía todo el mundo antes de los permisos.
   */
  esMiembro: boolean;
  /** El dueño anotado, para "pedíselo a…". `null` si no hay o si sos vos. */
  dueno: ContactoDelDueno | null;
}

const VENCE_MS = 2 * 60 * 1000;
let cache: { promesa: Promise<CuentaYPermisos>; desde: number } | null = null;
/** `true` = cambió el usuario: lo que el hook tiene es de otra cuenta y se tira antes de volver a leer. */
const suscriptos = new Set<(cambioDeUsuario: boolean) => void>();

function nombreVisible(p: { name: string | null; email: string | null }): string {
  return p.name?.trim() || (p.email ?? '').split('@')[0] || 'el administrador de la empresa';
}

async function cargar(): Promise<CuentaYPermisos> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { permisos: TODO_PERMITIDO, esDueno: false, esMiembro: false, dueno: null };
  const { data: perfil } = await supabase.from('profiles').select('id_company').eq('id', user.id).maybeSingle();
  const idCompany = (perfil as { id_company?: string | null } | null)?.id_company ?? null;
  if (!idCompany) return { permisos: TODO_PERMITIDO, esDueno: false, esMiembro: false, dueno: null };

  const [{ data: empresa, error: errorEmpresa }, { data: fila }] = await Promise.all([
    supabase.from('company_profiles').select('owner_user_id').eq('id_company', idCompany).maybeSingle(),
    supabase
      .from('permisos_de_miembro')
      .select('puede_publicar_en_tienda, marcas')
      .eq('user_id', user.id)
      .maybeSingle(),
  ]);
  const empresaRow = errorEmpresa ? null : ((empresa as { owner_user_id: string | null } | null) ?? null);
  const esDueno = esDuenoDeLaCuenta(empresaRow, user.id);
  const f = fila as { puede_publicar_en_tienda: boolean; marcas: string[] | null } | null;
  // El dueño nunca tiene restricciones (misma regla que el backend: `permisosEfectivos`).
  const permisos: MisPermisos =
    f && !esDueno
      ? { puedePublicarEnTienda: f.puede_publicar_en_tienda, marcas: f.marcas && f.marcas.length > 0 ? f.marcas : null }
      : TODO_PERMITIDO;

  let dueno: ContactoDelDueno | null = null;
  const ownerId = empresaRow?.owner_user_id ?? null;
  if (ownerId && ownerId !== user.id) {
    // La RLS de `profiles` deja ver a los de la propia empresa (20260912200000_profiles_rls.sql).
    const { data: p } = await supabase.from('profiles').select('name, email').eq('id', ownerId).maybeSingle();
    if (p) {
      const fila = p as { name: string | null; email: string | null };
      dueno = { nombre: nombreVisible(fila), email: fila.email };
    }
  }
  return { permisos, esDueno, esMiembro: !!ownerId && ownerId !== user.id, dueno };
}

/** Permisos propios + el dueño, compartidos entre todas las pantallas (vencen a los 2 minutos). */
export function cuentaYPermisos(ahora: number = Date.now()): Promise<CuentaYPermisos> {
  if (cache && ahora - cache.desde < VENCE_MS) return cache.promesa;
  const promesa = cargar().catch((err) => {
    if (cache?.promesa === promesa) cache = null;
    throw err;
  });
  cache = { promesa, desde: ahora };
  return promesa;
}

/** Después de que el dueño cambia permisos (o alguien entra o sale del equipo). */
export function olvidarPermisos(cambioDeUsuario = false): void {
  cache = null;
  for (const avisar of suscriptos) avisar(cambioDeUsuario);
}

/**
 * Los permisos son de UNA persona: si la sesión cambia de usuario sin recargar (el login de
 * otra cuenta en otra pestaña), lo guardado era del anterior. Mismo criterio que
 * `useTiendaConectada` (ADR-2026-10-21-revision-greptile-196-los-permisos-tienen-dueno).
 */
let dueno: string | null | undefined;
export function alCambiarDeUsuario(uid: string | null): void {
  if (uid === dueno) return;
  const habiaOtro = dueno !== undefined;
  dueno = uid;
  if (habiaOtro) olvidarPermisos(true);
}

if (typeof window !== 'undefined') {
  // `INITIAL_SESSION` fija el dueño sin tirar nada; el resto se difiere fuera del callback
  // para no llamar a la API de auth desde adentro.
  supabase.auth.onAuthStateChange((evento, sesion) => {
    const uid = sesion?.user?.id ?? null;
    if (evento === 'INITIAL_SESSION') {
      dueno = uid;
      return;
    }
    setTimeout(() => alCambiarDeUsuario(uid), 0);
  });
}

export function useCuentaYPermisos(): CuentaYPermisos & { cargando: boolean } {
  const [estado, setEstado] = useState<CuentaYPermisos | null>(null);
  const [vuelta, setVuelta] = useState(0);
  useEffect(() => {
    const otraVez = (cambioDeUsuario?: boolean) => {
      if (cambioDeUsuario === true) setEstado(null);
      setVuelta((n) => n + 1);
    };
    const alVolver = () => otraVez();
    suscriptos.add(otraVez);
    window.addEventListener('focus', alVolver);
    return () => {
      suscriptos.delete(otraVez);
      window.removeEventListener('focus', alVolver);
    };
  }, []);
  useEffect(() => {
    let vivo = true;
    cuentaYPermisos()
      .then((r) => vivo && setEstado(r))
      // Si no se pudo leer, no se esconde nada: el backend es el que dice que no.
      .catch(() => vivo && setEstado({ permisos: TODO_PERMITIDO, esDueno: false, esMiembro: false, dueno: null }));
    return () => {
      vivo = false;
    };
  }, [vuelta]);
  return estado
    ? { ...estado, cargando: false }
    : { permisos: TODO_PERMITIDO, esDueno: false, esMiembro: false, dueno: null, cargando: true };
}

/** "Pedíselo a Mati (mati@x.com)" — o, sin dueño conocido, "al dueño de la cuenta". */
export function aQuienPedirselo(dueno: ContactoDelDueno | null): string {
  if (!dueno) return 'al administrador de la empresa';
  return dueno.email ? `a ${dueno.nombre} (${dueno.email})` : `a ${dueno.nombre}`;
}

export function mensajeSinPermisoDeTienda(dueno: ContactoDelDueno | null): string {
  return `No tenés permiso para publicar en la tienda online. Pedíselo ${aQuienPedirselo(dueno)}.`;
}

export class SinPermisoDeTiendaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SinPermisoDeTiendaError';
  }
}

/**
 * Para los servicios que publican en la tienda: corta ANTES de pedir, con un mensaje que dice
 * a quién pedírselo. Si no se pueden leer los permisos, deja pasar: el backend decide.
 */
export async function exigirPermisoDeTienda(): Promise<void> {
  let r: CuentaYPermisos;
  try {
    r = await cuentaYPermisos();
  } catch {
    return;
  }
  if (!r.permisos.puedePublicarEnTienda) throw new SinPermisoDeTiendaError(mensajeSinPermisoDeTienda(r.dueno));
}

/** Los códigos del backend (`sin_permiso_tienda`, `marca_no_permitida`) en el copy del front. */
export function copyDeErrorDePermiso(codigo: string | null | undefined, dueno: ContactoDelDueno | null = null): string | null {
  if (codigo === 'sin_permiso_tienda') return mensajeSinPermisoDeTienda(dueno);
  if (codigo === 'marca_no_permitida') return `No tenés acceso a esa marca. Pedíselo ${aQuienPedirselo(dueno)}.`;
  return null;
}
